import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchUnitTypes,
  createUnitType,
  updateUnitType,
  deleteUnitType,
  getNextUnitTypeLabel,
  getUnitTypeUsageCount,
} from '../api/unitTypesApi'
import ImageUpload from '@/components/ImageUpload'

export default function UnitTypesManager({ projectId }) {
  const queryClient = useQueryClient()
  const [editingType, setEditingType] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [error, setError] = useState(null)

  const [formData, setFormData] = useState({
    label: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    car_parks: '',
    internal_size_sqm: '',
    default_sale_price: '',
    rent_per_week: '',
    floor_plan_url: '',
  })

  const { data: unitTypesData, isLoading } = useQuery({
    queryKey: ['unit-types', projectId],
    queryFn: () => fetchUnitTypes(projectId),
  })

  const unitTypes = unitTypesData?.data || []

  const createMutation = useMutation({
    mutationFn: createUnitType,
    onSuccess: () => {
      queryClient.invalidateQueries(['unit-types', projectId])
      resetForm()
      setShowCreateForm(false)
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to create unit type')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateUnitType(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['unit-types', projectId])
      setEditingType(null)
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to update unit type')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteUnitType,
    onSuccess: () => {
      queryClient.invalidateQueries(['unit-types', projectId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to delete unit type')
    },
  })

  const handleCreateNew = async () => {
    const { data: nextLabel } = await getNextUnitTypeLabel(projectId)
    setFormData({
      label: nextLabel || 'A',
      bedrooms: '',
      bathrooms: '',
      garages: '',
      car_parks: '',
      internal_size_sqm: '',
      default_sale_price: '',
      rent_per_week: '',
      floor_plan_url: '',
    })
    setShowCreateForm(true)
    setError(null)
  }

  const handleEdit = (unitType) => {
    setEditingType(unitType.id)
    setFormData({
      label: unitType.label,
      bedrooms: unitType.bedrooms?.toString() || '',
      bathrooms: unitType.bathrooms?.toString() || '',
      garages: unitType.garages?.toString() || '',
      car_parks: unitType.car_parks?.toString() || '',
      internal_size_sqm: unitType.internal_size_sqm?.toString() || '',
      default_sale_price: unitType.default_sale_price?.toString() || '',
      rent_per_week: unitType.rent_per_week?.toString() || '',
      floor_plan_url: unitType.floor_plan_url || '',
    })
  }

  const handleDelete = async (unitType) => {
    const { data: usageCount } = await getUnitTypeUsageCount(unitType.id)
    if (usageCount > 0) {
      if (!confirm(`This unit type is used by ${usageCount} units. Delete anyway?`)) {
        return
      }
    }
    deleteMutation.mutate(unitType.id)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)

    const payload = {
      project_id: projectId,
      label: formData.label,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseFloat(formData.bathrooms) || 0,
      garages: parseInt(formData.garages) || 0,
      car_parks: parseInt(formData.car_parks) || 0,
      internal_size_sqm: formData.internal_size_sqm ? parseFloat(formData.internal_size_sqm) : null,
      default_sale_price: formData.default_sale_price ? parseFloat(formData.default_sale_price) : null,
      rent_per_week: formData.rent_per_week ? parseFloat(formData.rent_per_week) : null,
      floor_plan_url: formData.floor_plan_url || null,
      display_order: unitTypes.length,
    }

    if (editingType) {
      updateMutation.mutate({ id: editingType, updates: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const resetForm = () => {
    setFormData({
      label: '',
      bedrooms: '',
      bathrooms: '',
      garages: '',
      car_parks: '',
      internal_size_sqm: '',
      default_sale_price: '',
      rent_per_week: '',
      floor_plan_url: '',
    })
    setEditingType(null)
    setShowCreateForm(false)
    setError(null)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFloorPlanUpload = (urls) => {
    if (urls && urls.length > 0) {
      setFormData((prev) => ({ ...prev, floor_plan_url: urls[0] }))
    }
  }

  const handleRemoveFloorPlan = () => {
    setFormData((prev) => ({ ...prev, floor_plan_url: '' }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Unit Types</h3>
          <p className="text-sm text-secondary-600">
            Define templates for unit configurations
          </p>
        </div>
        {!showCreateForm && (
          <button onClick={handleCreateNew} className="btn-primary">
            Add Unit Type
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {(showCreateForm || editingType) && (
        <form onSubmit={handleSubmit} className="card p-6 space-y-4">
          <h4 className="font-semibold text-secondary-900">
            {editingType ? 'Edit Unit Type' : 'Create Unit Type'}
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="label" className="label">
                Label *
              </label>
              <input
                id="label"
                name="label"
                type="text"
                value={formData.label}
                onChange={handleChange}
                className="input"
                required
                placeholder="A"
              />
            </div>

            <div>
              <label htmlFor="bedrooms" className="label">
                Bedrooms *
              </label>
              <input
                id="bedrooms"
                name="bedrooms"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="bathrooms" className="label">
                Bathrooms *
              </label>
              <input
                id="bathrooms"
                name="bathrooms"
                type="number"
                min="0"
                step="0.5"
                value={formData.bathrooms}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="garages" className="label">
                Garages
              </label>
              <input
                id="garages"
                name="garages"
                type="number"
                min="0"
                value={formData.garages}
                onChange={handleChange}
                className="input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="car_parks" className="label">
                Car Parks
              </label>
              <input
                id="car_parks"
                name="car_parks"
                type="number"
                min="0"
                value={formData.car_parks}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="internal_size_sqm" className="label">
                Internal Size (sqm)
              </label>
              <input
                id="internal_size_sqm"
                name="internal_size_sqm"
                type="number"
                min="0"
                step="0.1"
                value={formData.internal_size_sqm}
                onChange={handleChange}
                className="input"
                placeholder="e.g., 85.5"
              />
            </div>

            <div>
              <label htmlFor="default_sale_price" className="label">
                Default Price ($)
              </label>
              <input
                id="default_sale_price"
                name="default_sale_price"
                type="number"
                min="0"
                value={formData.default_sale_price}
                onChange={handleChange}
                className="input"
                placeholder="e.g., 750000"
              />
            </div>

            <div>
              <label htmlFor="rent_per_week" className="label">
                Rent per Week ($)
              </label>
              <input
                id="rent_per_week"
                name="rent_per_week"
                type="number"
                min="0"
                step="1"
                value={formData.rent_per_week}
                onChange={handleChange}
                className="input"
                placeholder="e.g., 550"
              />
            </div>
          </div>

          <div>
            <label className="label">Floor Plan</label>
            {formData.floor_plan_url ? (
              <div className="space-y-3">
                <div className="relative inline-block">
                  <img
                    src={formData.floor_plan_url}
                    alt="Floor plan"
                    className="max-w-full h-auto rounded-lg border border-secondary-200 max-h-64 object-contain"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveFloorPlan}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFloorPlan}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove floor plan
                </button>
              </div>
            ) : (
              <ImageUpload
                onUploadComplete={handleFloorPlanUpload}
                folder="floor-plans"
                maxFiles={1}
                existingImages={[]}
              />
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-secondary-200">
            <button type="button" onClick={resetForm} className="btn-outline flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary flex-1"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editingType
                ? 'Update'
                : 'Create'}
            </button>
          </div>
        </form>
      )}

      {unitTypes.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-secondary-400 mb-3">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-secondary-900 mb-1">
            No unit types yet
          </h4>
          <p className="text-secondary-600 mb-4">
            Create unit types to quickly configure multiple units with the same specifications
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {unitTypes.map((unitType) => (
            <div key={unitType.id} className="card p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg font-bold text-lg">
                  {unitType.label}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(unitType)}
                    className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-secondary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(unitType)}
                    className="p-1.5 hover:bg-red-50 rounded transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {unitType.floor_plan_url && (
                <div className="mb-3">
                  <img
                    src={unitType.floor_plan_url}
                    alt={`Floor plan for unit type ${unitType.label}`}
                    className="w-full h-40 object-cover rounded-lg border border-secondary-200"
                  />
                </div>
              )}

              <div className="space-y-2 text-sm text-secondary-700">
                <div className="flex justify-between">
                  <span className="text-secondary-600">Bedrooms:</span>
                  <span className="font-medium">{unitType.bedrooms}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Bathrooms:</span>
                  <span className="font-medium">{unitType.bathrooms}</span>
                </div>
                {unitType.garages > 0 && (
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Garages:</span>
                    <span className="font-medium">{unitType.garages}</span>
                  </div>
                )}
                {unitType.car_parks > 0 && (
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Car Parks:</span>
                    <span className="font-medium">{unitType.car_parks}</span>
                  </div>
                )}
                {unitType.internal_size_sqm && (
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Size:</span>
                    <span className="font-medium">{unitType.internal_size_sqm} sqm</span>
                  </div>
                )}
                {unitType.default_sale_price && (
                  <div className="flex justify-between pt-2 border-t border-secondary-200">
                    <span className="text-secondary-600">Default Price:</span>
                    <span className="font-semibold text-primary-700">
                      ${unitType.default_sale_price.toLocaleString()}
                    </span>
                  </div>
                )}
                {unitType.rent_per_week && (
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Rent per Week:</span>
                    <span className="font-medium text-mint-700">
                      ${unitType.rent_per_week.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
